import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Enhanced skeleton with better patterns
export function EnhancedSkeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Skeleton 
      className={cn("animate-pulse rounded-md bg-muted", className)} 
      {...props} 
    />
  );
}

// Page loading component
interface PageLoadingProps {
  title?: string;
  description?: string;
  className?: string;
}

export function PageLoading({ title = "Loading", description = "Please wait...", className }: PageLoadingProps) {
  return (
    <div className={cn("flex min-h-[400px] items-center justify-center", className)}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

// Inline loading component
interface InlineLoadingProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
}

export function InlineLoading({ size = 'md', text, className }: InlineLoadingProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Loader2 className={cn("animate-spin", sizeClasses[size])} />
      {text && <span className={textSizeClasses[size]}>{text}</span>}
    </div>
  );
}

// Button loading state
interface LoadingButtonProps {
  loading?: boolean;
  children: React.ReactNode;
  loadingText?: string;
}

export function LoadingButton({ loading, children, loadingText, ...props }: LoadingButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...props} disabled={loading || props.disabled}>
      {loading ? (
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {loadingText || children}
        </div>
      ) : (
        children
      )}
    </button>
  );
}

// Card skeleton
export function CardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <EnhancedSkeleton className="h-6 w-3/4" />
        <EnhancedSkeleton className="h-4 w-1/2" />
      </CardHeader>
      <CardContent className="space-y-3">
        <EnhancedSkeleton className="h-4 w-full" />
        <EnhancedSkeleton className="h-4 w-5/6" />
        <EnhancedSkeleton className="h-4 w-4/6" />
      </CardContent>
    </Card>
  );
}

// Table skeleton
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {Array.from({ length: cols }).map((_, i) => (
          <EnhancedSkeleton key={i} className="h-6" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array.from({ length: cols }).map((_, j) => (
            <EnhancedSkeleton key={j} className="h-4" />
          ))}
        </div>
      ))}
    </div>
  );
}

// Chart skeleton
export function ChartSkeleton() {
  return (
    <Card>
      <CardHeader>
        <EnhancedSkeleton className="h-6 w-1/3" />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between">
            {Array.from({ length: 5 }).map((_, i) => (
              <EnhancedSkeleton key={i} className="h-32 w-8" />
            ))}
          </div>
          <div className="flex justify-between">
            {Array.from({ length: 5 }).map((_, i) => (
              <EnhancedSkeleton key={i} className="h-4 w-8" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}